import Seo from "../components/Seo";
import { MarketingHeader, CTASection, FAQBlock } from "../components/MarketingShell";
import LegalFooter from "../components/LegalFooter";
import { Zap, Video, Edit3, Download, Globe, ShieldCheck, Clock, Sparkles } from "lucide-react";

const FEATURES = [
  { icon: Globe, title: "21 languages, any direction",
    body: "Hindi, Bhojpuri, Magahi, Awadhi, Urdu, Tamil, Telugu, Bengali, Malayalam, Gujarati, Punjabi, Marathi — plus English, Chinese, French, German, Russian, Japanese, Spanish, Korean, Italian. Pick any two, swap with one click." },
  { icon: Sparkles, title: "Personal-tone translation",
    body: "Powered by GPT-5.4 with a subtitle-tuned prompt. 'अरे यार, तूने मेरा दिल तोड़ दिया' becomes 'Damn, you really broke me' — not 'Oh friend, you broke my heart'." },
  { icon: Video, title: "Burn-in MP4 export",
    body: "One click renders your video with subtitles baked into the frames. Ready to upload to YouTube, Instagram Reels, LinkedIn, or WhatsApp without a separate .srt file." },
  { icon: Edit3, title: "Live subtitle editor",
    body: "Play the video, click any line to pause and jump to it, edit the translation inline, tweak start/end timestamps, and save. Re-render whenever you're happy." },
  { icon: Download, title: "SRT + VTT export",
    body: "Standard formats that work with YouTube Studio, Vimeo, Premiere Pro, DaVinci Resolve, Final Cut, and every hosting platform. Copy to clipboard or download the file." },
  { icon: Zap, title: "5 GB uploads",
    body: "Up to 5 GB per file — long-form documentaries, wedding videos, podcast episodes, 2-hour interviews. Whisper chunks the audio internally so long clips still finish fast." },
  { icon: Clock, title: "48-hour project storage",
    body: "Uploaded video stays available for 48 hours so you can iterate — edit subtitles, re-render, re-download. Auto-deleted after that unless you re-upload." },
  { icon: ShieldCheck, title: "Google + email sign-in",
    body: "Sign in with Google in one click, or use a plain email + password. Sessions last 7 days. No third-party trackers, no ad pixels." },
];

const FAQ = [
  { q: "Does it really translate Bhojpuri and Awadhi?", a: "Yes. Whisper doesn't have first-class models for Bhojpuri, Magahi or Awadhi, but LinguaSub feeds it a Hindi model with a dialect-aware prompt so the transcription is usable, then the GPT-5.4 translation cleans up dialect-specific expressions. Accuracy is solid on clean audio; noisy village-recorded audio benefits from a manual editor pass." },
  { q: "What file formats do you accept?", a: "Video: MP4, MOV, WEBM, MKV, AVI. Audio: MP3, WAV, M4A, OGG, FLAC. Text: paste directly. Max 5 GB per upload." },
  { q: "Can I use LinguaSub commercially?", a: "Yes. Any paid plan gives you commercial rights to the output. Free-tier output is fine for personal, educational, and open-source use. See /terms." },
  { q: "Do you support subtitle styling — font, colour, position?", a: "The burn-in uses a default readable style (white text, black outline, 40px margin from bottom). A style picker is on the roadmap — reply to us on the feedback wall if you want it prioritised." },
  { q: "How accurate is the automatic transcription?", a: "For clean audio in Hindi, Tamil, Bengali, English: ~95%. For Bhojpuri/Awadhi/Magahi: ~80% baseline, higher after our dialect prompt. Always review the segments in the editor before shipping to your audience." },
];

export default function FeaturesPage() {
  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white">
      <Seo path="/features" title="Features" description="LinguaSub features — 21 languages any-to-any, personal-tone GPT translation, burn-in subtitled video export, live subtitle editor, SRT + VTT downloads, 5 GB uploads." />
      <MarketingHeader />
      <section className="relative border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-3">Product tour</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter leading-tight mb-6 max-w-4xl">
            Everything you get in one screen.
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed">
            LinguaSub is a full subtitle studio in the browser — transcribe, translate with personality, edit against the video, and export the finished subtitled clip. No timeline software required.
          </p>
        </div>
      </section>
      <section className="border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="border border-zinc-800 bg-zinc-900/40 p-6 hover:border-blue-500/40 transition-colors">
                <f.icon className="w-6 h-6 text-blue-400 mb-4" />
                <h3 className="font-heading text-lg font-bold text-white mb-2">{f.title}</h3>
                <p className="text-xs text-zinc-400 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Example strip */}
      <section className="border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">Real output</div>
          <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tighter mb-8 text-white">
            The tone survives the translation.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { src: "क्यों बे, तूने कर दिया कमाल! (Hindi, sarcastic)", out: "Oh wow, look at you go. (English)" },
              { src: "நண்பா, ஏன் இப்படி பண்ற? (Tamil, hurt)", out: "Buddy, why are you doing this to me? (English)" },
              { src: "ভাই, চাপ নিও না। (Bengali, reassuring)", out: "Bro, don't stress it. (English)" },
              { src: "Zindagi ek safar hai suhana (Hinglish)", out: "Life's a beautiful ride (English)" },
            ].map((ex) => (
              <div key={ex.src} className="border border-zinc-800 bg-zinc-900/40 p-5">
                <div className="text-xs text-zinc-500 font-mono mb-2">{ex.src}</div>
                <div className="text-base text-white">→ {ex.out}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQBlock items={FAQ} />
      <CTASection />
      <div className="max-w-6xl mx-auto px-4 md:px-8"><LegalFooter /></div>
    </div>
  );
}
